import domready from "domready"
// noinspection ES6UnusedImports
import STYLE from "./style.css"
import Color from "./Color";
import SimplexNoise from "simplex-noise"
import { clamp } from "./util";
import Gradient from "./Gradient";
import now from "performance-now";


const PHI = (1 + Math.sqrt(5)) / 2;
const TAU = Math.PI * 2;
const DEG2RAD_FACTOR = TAU / 360;

const HORIZON_FACTOR = 0.5; // 1 - 1 / PHI;

const config = {
    width: 0,
    height: 0,
    horizonLine: 0
};

let ctx, canvas;

const noise = new SimplexNoise();



function drawSky(data)
{
    let off = 0;

    const start = Color.from("#339cf8");
    const end = Color.from("#662e2e");
    //const end = Color.from("#ffcc00");
    const curr = new Color(0, 128, 255);

    let min = Infinity, max = -Infinity;

    const {width, height, horizonLine} = config;

    const sunX = Math.random() * width;
    const sunY = horizonLine;

    const sunSize = width * 0.75;

    for (let y = 0; y < horizonLine; y++)
    {
        for (let x = 0; x < width; x++)
        {
            const ratio = clamp((y / horizonLine));
            start.mix(end, ratio, curr);

            const dx = sunX - x;
            const dy = sunY - y;

            const sunDist = Math.sqrt(dx*dx+dy*dy);

            curr.mix(SUN_COLOR, clamp(0.8 - sunDist / sunSize), curr);

            data[off++] = curr.r;
            data[off++] = curr.g;
            data[off++] = curr.b;
            data[off++] = 255;

            min = Math.min(min, ratio);
            max = Math.max(max, ratio);

        }
    }

    console.log({min, max})
}

const CLOUD_GRADIENT = new Gradient([
    Color.from("#ffffff"),
    Color.from("#d9d4aa"),
    Color.from("#545cae")
], 16);

const LANDSCAPE_GRADIENT = new Gradient([
    Color.from("#4b5b4f"),
    Color.from("#412020")
], 16);

const SUN_COLOR = Color.from("#efd786");

const mix = new Color(0,0,0);

const brushes = new Map();


function createAlphaBrush(radius)
{
    const brush = [];
    const sqRadius = radius * radius;

    for (let y = - radius; y < radius; y++)
    {
        const w = Math.sqrt( sqRadius - y * y );
        brush.push(y,w);
        for (let x = -w; x < w; x++)
        {

            if (Math.random() > 0.3)
            {
                const opacity = Math.sqrt(x * x + y * y) / radius;
                brush.push(clamp( 1 - Math.pow(opacity, 3)));
            }
            else
            {
                brush.push(Math.random());
            }
        }
    }

    return brush;
}

const tmp0 = new Color(0,0,0);
const tmp1 = new Color(0,0,0);
const tmp2 = new Color(0,0,0);
const tmp3 = new Color(0,0,0);
const tmp4 = new Color(0,0,0);

function fillCircle(data, cx, cy, radius, color, ratio)
{
    cx |=0;
    cy |=0;

    let brush = brushes.get(radius);
    if (!brushes.has(radius))
    {
        brush = createAlphaBrush(radius);
        brushes.set(radius, brush)
    }

    const { width } = config;

    let pos = 0;

    const { length : brushLength } = brush;

    while (pos < brushLength)
    {
        const y = brush[pos++];
        const w = brush[pos++];

        let offset = ((cy + y) * width + cx - w) << 2;
        let offsetL = ((cy + y) * width + cx - w - 1) << 2;
        let offsetR = ((cy + y) * width + cx - w + 1) << 2;
        let offsetT = ((cy + y - 1) * width + cx - w) << 2;
        let offsetB = ((cy + y + 1) * width + cx - w) << 2;

        for (let x = -w; x < w; x++)
        {
            const value = brush[pos++];

            if (value > 0)
            {
                tmp0.pick(data, offset);
                tmp1.pick(data, offsetL);
                tmp2.pick(data, offsetR);
                tmp3.pick(data, offsetT);
                tmp4.pick(data, offsetB);

                tmp0.r = (tmp0.r + tmp1.r + tmp2.r + tmp3.r + tmp4.r)/5;
                tmp0.g = (tmp0.g + tmp1.g + tmp2.g + tmp3.g + tmp4.g)/5;
                tmp0.b = (tmp0.b + tmp1.b + tmp2.b + tmp3.b + tmp4.b)/5;

                color.mix(tmp0, 1 - (ratio * value), mix);
                mix.draw(data, offset);
            }

            offset += 4;
            offsetL += 4;
            offsetR += 4;
            offsetT += 4;
            offsetB += 4;
        }
    }
}

const tmpCloud = new Color(0,0,0);
const BLUE = Color.from("#bae5ff");

function drawCloud(data, cx, cy , scale)
{
    const max = (50 + Math.random() * 50) * scale;

    for (let i = 0; i < max; i+=4)
    {
        const x = Math.random() * max;
        const rnd = Math.random();

        BLUE.mix(CLOUD_GRADIENT.colorAt(rnd), 0.6 + scale * 0.3, tmpCloud);

        fillCircle(
            data,
            cx + x, cy + rnd * 20 * scale,
            10 * scale,
            tmpCloud, 0.2
        );
    }
}


function drawClouds(data)
{
    const {width, height, horizonLine} = config;

    const start = horizonLine * 0.8;

    let distStep = -900 / (start/8);

    const cloudHeight = horizonLine * .15 * 1000;

    for (let dist = 1000; dist > 100; dist -= 50)
    {
        const size = 500 / dist;
        const y = horizonLine -  cloudHeight / dist;

        for (let x = 0; x < 100/size; x++)
        {
            const cx = Math.random() * width; //- 100 * size / dist;
            const cy = y + (Math.random() * 10000 / dist);

            const n = noise.noise2D(cx * 0.004, cy * 0.004);
            if (Math.abs(n) > 0.45)
            {
                drawCloud(data, cx, cy, size);
            }
        }
    }

}


const MOUNTAIN_LAYERS = 3;
const MOUNTAIN_HEIGHT = 100;
const MOUNTAIN_FALLDOWN = 0.8;
const RESOLUTION = 8;


function getRandoms(num)
{
    const array = new Array(num);
    for (let i=0; i < num; i++)
    {
        array[i] = Math.random();
    }
    return array;
}



function drawMountains(data)
{
    const {width, height, horizonLine} = config;



    const delta = height - horizonLine;
    const step = delta / MOUNTAIN_LAYERS;

    for (let y = horizonLine; y < height ; y += step)
    {
        ctx.fillStyle = LANDSCAPE_GRADIENT.colorAt(1 - (height - y) / delta).toRGB();

        const length = (width + RESOLUTION / RESOLUTION)|0;
        const heights = new Array(length);

        let lo=0;
        let hi=length - 1;

        heights[lo] = Math.random();
        heights[hi] = Math.random();

        const displacePoint = (lo, hi, offset) => {
            const mid = (lo + hi) >> 1;
            heights[mid] = (heights[lo] + heights[hi]) / 2 + ( -0.5 + Math.random()) * offset;

            if (mid - lo > 1)
            {
                displacePoint(lo, mid, offset * MOUNTAIN_FALLDOWN);
            }
            if (hi - mid > 1)
            {
                displacePoint(mid, hi, offset * MOUNTAIN_FALLDOWN);
            }
        };

        displacePoint(lo, hi, 0.5);

        ctx.beginPath();
        ctx.moveTo(0, y);
        for (let i = 0; i < heights.length; i++)
        {
            const h = heights[i];

            ctx.lineTo(i * RESOLUTION, y - h * MOUNTAIN_HEIGHT);
            console.log(i * RESOLUTION, y - h * MOUNTAIN_HEIGHT);
        }
        ctx.lineTo((heights.length - 1) * RESOLUTION, y + step)
        ctx.lineTo(0, y + step)
        ctx.lineTo(0, y);
        ctx.fill();

    }
}


domready(
    () => {
        canvas = document.getElementById("screen");
        ctx = canvas.getContext("2d");

        config.width = (window.innerWidth) | 0;
        config.height = (window.innerHeight) | 0;

        canvas.width = config.width;
        canvas.height = config.height;
        config.horizonLine = (config.height * HORIZON_FACTOR) | 0;

        const imageData = ctx.createImageData(config.width, config.height);
        const {data} = imageData;

        const start = now();

        drawSky(data);
        drawClouds(data);
        ctx.putImageData(imageData, 0, 0);
        //drawMountains(data);

        console.log(`Rendered in ${now() - start}ms`, brushes);


    }
);
